/**
 * LiveClassArchivePage — student-facing archive of a published completed class.
 *
 * Layout:
 *   [Back button]
 *   [Header card: title, date, host, grade]
 *   [Recording embed]        (if recording_embed_html)
 *   [Topics summary]         (if topics_summary)
 *   [Q&A archive read-only]  (all questions, sorted, with faculty replies)
 *
 * URL: /live-classes/:classId/archive
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Calendar, Clock, User as UserIcon, MessagesSquare,
  Loader2, AlertCircle, PlayCircle, ThumbsUp, CheckCircle2, Pin,
  Reply, Search, Archive,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtIST = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
};

// ---------- Archived question card (read-only) ----------
const ArchivedQuestionCard = ({ q }) => (
  <Card
    className={`${q.is_pinned ? 'border-2 border-amber-300 bg-amber-50/40' : ''} ${q.is_answered ? 'border-l-4 border-l-emerald-500' : ''}`}
    data-testid={`archive-question-${q.id}`}
  >
    <CardContent className="p-4">
      <div className="flex gap-4">
        {/* Upvote count (read-only) */}
        <div className="flex flex-col items-center gap-1 min-w-[52px]">
          <div className="w-11 h-11 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-500">
            <ThumbsUp className="w-4 h-4" />
          </div>
          <span className="text-sm font-semibold tabular-nums text-gray-700">{q.upvote_count}</span>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            {q.is_pinned && (
              <Badge className="bg-amber-500 text-white border-0 gap-1">
                <Pin className="w-3 h-3" /> Pinned
              </Badge>
            )}
            {q.is_answered && (
              <Badge className="bg-emerald-600 text-white border-0 gap-1">
                <CheckCircle2 className="w-3 h-3" /> Answered
              </Badge>
            )}
            {q.is_anonymous && (
              <Badge variant="outline" className="text-xs text-gray-600">Anonymous</Badge>
            )}
          </div>
          <p className="text-sm text-gray-900 whitespace-pre-wrap">{q.text}</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <UserIcon className="w-3 h-3" />
            <span>{q.author_name}</span>
            <span>·</span>
            <span>{fmtIST(q.created_at)}</span>
          </div>

          {q.host_reply && (
            <div className="mt-3 pl-3 border-l-2 border-[#0A7EA4] bg-[#0A7EA4]/5 py-2 pr-3 rounded-r">
              <div className="flex items-center gap-2 text-xs font-semibold text-[#0A7EA4] mb-0.5">
                <Reply className="w-3 h-3" />
                <span>{q.host_reply.replied_by_name} · Faculty</span>
              </div>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{q.host_reply.text}</p>
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const LiveClassArchivePage = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [qSearch, setQSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setState({ loading: true, error: '', data: null });
      const { data } = await axios.get(`${API}/live-classes/${classId}/archive`);
      setState({ loading: false, error: '', data });
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Failed to load archive');
      setState({ loading: false, error: msg, data: null });
    }
  }, [classId]);

  useEffect(() => { load(); }, [load]);

  const cls = state.data?.class;
  const questions = state.data?.questions || [];

  // Client-side filter over the loaded archive (server already returned all)
  const visibleQuestions = useMemo(() => {
    const term = qSearch.trim().toLowerCase();
    if (!term) return questions;
    return questions.filter(q => {
      if (q.text.toLowerCase().includes(term)) return true;
      if (q.host_reply?.text?.toLowerCase().includes(term)) return true;
      return false;
    });
  }, [questions, qSearch]);

  const backPath = location.state?.from || '/live-classes/archive';
  // Show a context-aware back label — coming from the archive listing should
  // say "Back to Class Archive", not "Back to Live Classes".
  const backLabel = backPath === '/live-classes/archive'
    ? 'Back to Class Archive'
    : 'Back to Live Classes';

  return (
    <Layout>
      <div className="page-transition max-w-5xl mx-auto" data-testid="live-class-archive-page">
        <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}
          className="mb-4 text-gray-600 hover:text-gray-900" data-testid="archive-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          {backLabel}
        </Button>

        {state.loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
        ) : state.error ? (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-900 mb-1">Archive unavailable</p>
                <p className="text-sm text-amber-800">{state.error}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ---------- Header ---------- */}
            <Card className="mb-5 border-l-4 border-l-emerald-500 shadow-sm">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-transparent">
                <div className="flex items-center gap-2 mb-1">
                  <Archive className="w-5 h-5 text-emerald-700" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Class archive</span>
                </div>
                <CardTitle className="text-2xl text-gray-900" data-testid="archive-title">{cls?.title}</CardTitle>
                {cls?.description && cls.description !== cls.title && (
                  <CardDescription className="text-sm mt-1">{cls.description}</CardDescription>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 mt-2">
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{fmtIST(cls?.start_time)}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{cls?.duration_minutes} min</span>
                  <span className="flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5" />{cls?.host_user_name}</span>
                  {Array.isArray(cls?.grades) && cls.grades.map(g => (
                    <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                  ))}
                </div>
              </CardHeader>
            </Card>

            {/* ---------- Recording ---------- */}
            {cls?.recording_embed_html ? (
              <Card className="mb-5 overflow-hidden" data-testid="archive-recording">
                <div className="px-5 py-3 border-b bg-gray-50 flex items-center gap-2">
                  <PlayCircle className="w-4 h-4 text-[#0A7EA4]" />
                  <span className="text-sm font-semibold text-gray-800">Class recording</span>
                </div>
                <CardContent className="p-0">
                  {/* Responsive 16:9 wrapper. Backend sanitizes the HTML — only
                      iframes from an allowlist of hosts (Dadan / YouTube / Vimeo /
                      Google Drive / Loom / Wistia) survive, with no scripts or
                      event handlers, so `dangerouslySetInnerHTML` is safe here. */}
                  <div
                    className="relative w-full [&>iframe]:absolute [&>iframe]:inset-0 [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0"
                    style={{ paddingBottom: '56.25%' }}
                    dangerouslySetInnerHTML={{ __html: cls.recording_embed_html }}
                  />
                </CardContent>
              </Card>
            ) : null}

            {/* ---------- Topics summary ---------- */}
            {cls?.topics_summary && (
              <Card className="mb-5" data-testid="archive-topics-summary">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <div className="w-1 h-4 bg-[#0A7EA4] rounded-full" />
                    Topics discussed in this class
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {cls.topics_summary}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* ---------- Q&A archive ---------- */}
            <Card className="mb-5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MessagesSquare className="w-4 h-4 text-[#0A7EA4]" />
                      Questions asked before class
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      <span className="font-semibold text-gray-900">{state.data.total_questions}</span> total
                      {' · '}
                      <span className="font-semibold text-emerald-700">{state.data.answered_count}</span> answered
                    </CardDescription>
                  </div>
                  {questions.length > 0 && (
                    <div className="relative w-full sm:w-64">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        type="search"
                        placeholder="Filter these questions…"
                        value={qSearch}
                        onChange={(e) => setQSearch(e.target.value)}
                        className="pl-9 h-9 text-sm"
                        data-testid="archive-question-search"
                      />
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {questions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No questions were asked for this class.
                  </div>
                ) : visibleQuestions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    No questions match &ldquo;{qSearch}&rdquo;.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {visibleQuestions.map(q => (
                      <ArchivedQuestionCard key={q.id} q={q} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default LiveClassArchivePage;
